import { cn } from "@d12labs/utils";
import { Link } from "@tanstack/react-router";
import {
	ChevronLeft,
	Flame,
	LayoutGrid,
	Library,
	type LucideIcon,
	Mic,
	Presentation,
	Sparkles,
	StickyNote,
	Upload,
	Users,
} from "lucide-react";
import type { ReactNode } from "react";

/**
 * Shared building blocks for sidebars — used by the app shell's launcher/default
 * sidebars and by any plugin's `client/sidebar.tsx`. Keeping them here means
 * every sidebar (core or plugin) renders with identical spacing, type, and
 * active-state treatment.
 */

// Manifest `icon` strings → lucide components. Unlisted names fall back to the
// generic grid glyph; add a row to light them up.
const NAV_ICONS: Record<string, LucideIcon> = {
	Users,
	Sparkles,
	StickyNote,
	Presentation,
	Upload,
	Flame,
	Mic,
	Library,
};
export const navIcon = (name?: string): LucideIcon =>
	name ? (NAV_ICONS[name] ?? LayoutGrid) : LayoutGrid;

const NAV_ROW =
	"flex items-center gap-2.5 rounded-md px-2 py-1.5 text-body-sm font-medium transition-colors";

/** A small lavender brand tile holding an icon (matches Linear's workspace mark). */
export function BrandSquare({ icon: Icon }: { icon: LucideIcon }) {
	return (
		<span className="flex size-6 flex-none items-center justify-center rounded-md bg-primary text-primary-foreground">
			<Icon className="h-3.5 w-3.5" />
		</span>
	);
}

/** Sidebar header: optional back-to-launcher chevron, optional brand mark, title + subtitle. */
export function SidebarBrand({
	icon,
	title,
	subtitle,
	onBack,
}: {
	icon?: ReactNode;
	title: string;
	subtitle?: string;
	onBack?: () => void;
}) {
	return (
		<div className="flex h-[56px] flex-none items-center gap-2 border-b border-hairline px-3">
			{onBack ? (
				<button
					type="button"
					onClick={onBack}
					aria-label="Back to apps"
					className="flex size-6 flex-none items-center justify-center rounded-md text-ink-subtle outline-none transition-colors hover:bg-surface-1 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
				>
					<ChevronLeft className="h-4 w-4" />
				</button>
			) : null}
			{icon ?? null}
			<div className="flex min-w-0 flex-col leading-tight">
				<span className="truncate text-body-sm font-semibold tracking-tight">{title}</span>
				{subtitle ? <span className="truncate text-meta text-ink-subtle">{subtitle}</span> : null}
			</div>
		</div>
	);
}

/** A labeled group of sidebar rows. */
export function SidebarSection({ label, children }: { label?: string; children: ReactNode }) {
	return (
		<div className="flex flex-col gap-0.5">
			{label ? <div className="eyebrow px-2 pb-1 pt-3 text-ink-subtle">{label}</div> : null}
			{children}
		</div>
	);
}

/** A primary nav row: icon + label, with Linear's surface-2 active fill. */
export function SidebarNavItem({
	to,
	icon: Icon,
	label,
	exact = false,
	trailing,
	onNavigate,
}: {
	to: string;
	icon?: LucideIcon;
	label: string;
	/** Match only the exact path (use for a section's index/"home" route). */
	exact?: boolean;
	/** Right-aligned count or status. */
	trailing?: ReactNode;
	onNavigate?: () => void;
}) {
	return (
		<Link
			to={to}
			onClick={onNavigate}
			// includeSearch:false so a route's search params (e.g. CRM's ?q=&page=)
			// don't defeat the active match — we only care about the path here.
			activeOptions={{ exact, includeSearch: false }}
			activeProps={{ className: "bg-surface-2 text-foreground" }}
			inactiveProps={{ className: "text-ink-muted hover:bg-surface-1 hover:text-foreground" }}
			className={NAV_ROW}
		>
			{Icon ? <Icon className="h-4 w-4 flex-none opacity-90" /> : null}
			<span className="truncate">{label}</span>
			{trailing ? <span className="ml-auto flex-none text-meta">{trailing}</span> : null}
		</Link>
	);
}

/**
 * A nav row that runs an action instead of changing route (opens a drawer,
 * scrolls to a section). Same look as an inactive <SidebarNavItem>.
 */
export function SidebarActionItem({
	icon: Icon,
	label,
	trailing,
	onClick,
}: {
	icon?: LucideIcon;
	label: string;
	trailing?: ReactNode;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				NAV_ROW,
				"w-full text-left text-ink-muted outline-none hover:bg-surface-1 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50",
			)}
		>
			{Icon ? <Icon className="h-4 w-4 flex-none opacity-90" /> : null}
			<span className="truncate">{label}</span>
			{trailing ? <span className="ml-auto flex-none text-meta">{trailing}</span> : null}
		</button>
	);
}

/** A secondary nav row: a status dot + label (e.g. a tracked entity). */
export function SidebarSubItem({
	to,
	label,
	primary = false,
	trailing,
	onNavigate,
}: {
	to: string;
	label: string;
	/** Render the dot in the accent color (e.g. the primary speaker). */
	primary?: boolean;
	trailing?: ReactNode;
	onNavigate?: () => void;
}) {
	return (
		<Link
			to={to}
			onClick={onNavigate}
			activeOptions={{ exact: false, includeSearch: false }}
			activeProps={{ className: "text-foreground" }}
			inactiveProps={{ className: "text-ink-subtle hover:text-foreground" }}
			className="flex items-center gap-2.5 rounded-md py-1.5 pl-3 pr-2 text-body-sm transition-colors"
		>
			<span
				className={cn(
					"size-1.5 flex-none rounded-full",
					primary ? "bg-primary" : "bg-ink-tertiary",
				)}
			/>
			<span className="truncate">{label}</span>
			{trailing ? <span className="ml-auto flex-none">{trailing}</span> : null}
		</Link>
	);
}
